import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, uploadFile } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { useParams2 } from "../contexts/ParamsContext";
import RichEditor from "../components/RichEditor";
import ConfirmModal from "../components/ConfirmModal";
import BibliothequeAdmin from "./admin/BibliothequeAdmin";
import BarAdmin from "./admin/BarAdmin";
import OngletCartographie from "./admin/OngletCartographie";
import ConseilAdmin from "./admin/ConseilAdmin";
import SuiviAdmin from "./admin/SuiviAdmin";
import AssociationsAdmin from "./admin/AssociationsAdmin";
import ReservationsSalleAdmin from "./admin/ReservationsSalleAdmin";
import { buildMarkerIcon, STATUS_COLORS } from "./signalIcons";

const CATEGORIES = [
  ["actualite", "Actualité"],
  ["arrete", "Arrêté"], ["manifestation", "Manifestation"],
  ["vie_scolaire", "Vie scolaire"],
];
// Libelles de repli pour d'anciennes actus encore en base sous ces categories.
const CAT_LEGACY = { conseil_municipal: "Conseil municipal", information: "Information" };
const catLabel = (k) =>
  (CATEGORIES.find((c) => c[0] === k) || [])[1] || CAT_LEGACY[k] || k;

const TABS = [
  ["actualites", "Actualités"],
  ["evenements", "Calendrier"],
  ["signalements", "Signalements"],
  ["bibliotheque", "Bibliothèque"],
  ["bar", "Bar associatif"],
  ["groupes", "Groupes"],
  ["rubriques", "Rubriques"],
  ["documents", "Documents"],
  ["messages", "Messages"],
  ["parametres", "Paramètres"],
  ["utilisateurs", "Utilisateurs"],
  ["cartographie", "Cartographie"],
  ["conseil", "Conseil municipal"],
  ["suivi", "Suivi du site"],
  ["associations", "Associations"],
  ["reservations_salle", "Réservations salle"],
];

const UPLOADS = "/uploads/actualites/";
const DOCUMENTS_BASE = "/uploads/documents/";

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("actualites");

  const doLogout = async () => { await logout(); nav("/admin/login"); };

  return (
    <div className="container">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: "1rem" }}>
        <h1>Administration</h1>
        <div className="muted">
          {user?.prenom} {user?.nom}
          <button className="secondary small" style={{ marginLeft: "1rem" }} onClick={doLogout}>
            Déconnexion
          </button>
        </div>
      </div>

      <div className="tabs mt-2">
        {TABS.map(([k, label]) => (
          <button key={k} className={`tab ${tab === k ? "active" : ""}`} onClick={() => setTab(k)}>
            {label}
          </button>
        ))}
      </div>

      {tab === "actualites" && <ActualitesAdmin />}
      {tab === "evenements" && <EvenementsAdmin />}
      {tab === "signalements" && <SignalementsAdmin />}
      {tab === "bibliotheque" && <BibliothequeAdmin />}
      {tab === "bar" && <BarAdmin />}
      {tab === "groupes" && <GroupesAdmin />}
      {tab === "rubriques" && <RubriquesAdmin />}
      {tab === "documents" && <DocumentsAdmin />}
      {tab === "messages" && <MessagesAdmin />}
      {tab === "parametres" && <ParametresAdmin />}
      {tab === "utilisateurs" && <UtilisateursAdmin meId={user?.id} />}
      {tab === "cartographie" && <OngletCartographie />}
      {tab === "conseil" && <ConseilAdmin />}
      {tab === "suivi" && <SuiviAdmin />}
      {tab === "associations" && <AssociationsAdmin />}
      {tab === "reservations_salle" && <ReservationsSalleAdmin />}
    </div>
  );
}

/* ====================== ACTUALITES ====================== */
function ActualitesAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [msg, setMsg] = useState("");
  const [filtre, setFiltre] = useState("actuels");  // actuels | archives

  const load = useCallback(async () => {
    try {
      const d = await api.get(`/admin/actualites?archive=${filtre}`);
      setItems(d.items || []);
    } catch {}
  }, [filtre]);
  useEffect(() => { load(); }, [load]);

  const empty = {
    titre: "", chapeau: "", contenu_html: "", categorie: "actualite",
    publie: true, epingle: false, date_publication: new Date().toISOString().slice(0, 10),
    date_expiration: "",
  };

  const remove = async () => {
    try { await api.del(`/admin/actualites/${confirmDel.id}`); setConfirmDel(null); load(); }
    catch (e) { setMsg(e.message); }
  };

  const toggleArchive = async (a) => {
    try {
      const action = a.archivee ? "desarchiver" : "archiver";
      await api.post(`/admin/actualites/${a.id}/${action}`);
      load();
    } catch (e) { setMsg(e.message); }
  };

  if (editing) {
    return <ActualiteForm initial={editing} onClose={() => { setEditing(null); load(); }} />;
  }

  return (
    <div>
      {msg && <div className="error">{msg}</div>}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.6rem" }}>
        <button onClick={() => setEditing(empty)} className="mb-2">+ Nouvelle actualité</button>
        <div className="actu-filter-tabs mb-2">
          <button className={filtre === "actuels" ? "active" : ""}
                  onClick={() => setFiltre("actuels")}>Actuelles</button>
          <button className={filtre === "archives" ? "active" : ""}
                  onClick={() => setFiltre("archives")}>Archivées</button>
        </div>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr><th>Titre</th><th>Catégorie</th><th>Date</th><th>Expiration</th><th>État</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id}>
                <td>{a.titre} {a.epingle ? "📌" : ""}</td>
                <td>{catLabel(a.categorie)}</td>
                <td>{a.date_publication}</td>
                <td className="muted">
                  {a.date_expiration ? new Date(a.date_expiration).toLocaleString("fr-FR", {
                    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
                  }) : "—"}
                </td>
                <td>
                  {a.archivee ? (
                    <span className="badge badge-brouillon">Archivée</span>
                  ) : (
                    <span className={`badge ${a.publie ? "badge-publie" : "badge-brouillon"}`}>
                      {a.publie ? "Publié" : "Brouillon"}
                    </span>
                  )}
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="secondary small" onClick={async () => {
                    const full = await api.get(`/admin/actualites/${a.id}`);
                    setEditing(full);
                  }}>Modifier</button>{" "}
                  <button className="secondary small" onClick={() => toggleArchive(a)}>
                    {a.archivee ? "Désarchiver" : "Archiver"}
                  </button>{" "}
                  <button className="danger small" onClick={() => setConfirmDel(a)}>Suppr.</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="muted">
              {filtre === "archives" ? "Aucune actualité archivée." : "Aucune actualité."}
            </td></tr>}
          </tbody>
        </table>
      </div>

      {confirmDel && (
        <ConfirmModal titre="Supprimer l'actualité" danger
          message={`Supprimer définitivement « ${confirmDel.titre} » et ses photos ?`}
          confirmLabel="Supprimer" onConfirm={remove} onCancel={() => setConfirmDel(null)} />
      )}
    </div>
  );
}

function RubriqueSelect({ value, onChange }) {
  const [rubs, setRubs] = useState([]);
  useEffect(() => {
    api.get("/admin/rubriques").then((d) => setRubs(d.items || [])).catch(() => {});
  }, []);
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)}>
      <option value="">— Aucune —</option>
      {rubs.map((r) => (
        <option key={r.cle} value={r.cle}>{r.titre}</option>
      ))}
    </select>
  );
}

function ActualiteForm({ initial, onClose }) {
  const [f, setF] = useState({
    titre: initial.titre || "", chapeau: initial.chapeau || "",
    contenu_html: initial.contenu_html || "", categorie: initial.categorie || "actualite",
    lien_rubrique: initial.lien_rubrique || "",
    publie: !!initial.publie, epingle: !!initial.epingle,
    date_publication: (initial.date_publication || new Date().toISOString().slice(0, 10)).slice(0, 10),
    date_expiration: (initial.date_expiration || "").slice(0, 16),
  });
  const [id, setId] = useState(initial.id || null);
  const [photos, setPhotos] = useState(initial.photos || []);
  const [documents, setDocuments] = useState(initial.documents || []);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  // Enregistre l'actu. En creation, on conserve le formulaire ouvert (on passe
  // en mode edition) pour permettre l'ajout des pieces jointes dans la foulee.
  const save = async ({ closeAfter }) => {
    setError(""); setInfo("");
    if (!f.titre.trim()) { setError("Le titre est obligatoire."); return null; }
    setBusy(true);
    try {
      let currentId = id;
      if (id) {
        await api.put(`/admin/actualites/${id}`, f);
      } else {
        const r = await api.post("/admin/actualites", f);
        currentId = r.id;
        setId(r.id);
      }
      if (closeAfter) { onClose(); }
      else { setInfo("Enregistré. Vous pouvez ajouter des photos et des PDF ci-dessous."); }
      return currentId;
    } catch (e) {
      setError(e.message || "Erreur lors de l'enregistrement.");
      return null;
    } finally {
      setBusy(false);
    }
  };

  // S'assure qu'un id existe avant un upload (enregistre silencieusement si besoin).
  const ensureId = async () => {
    if (id) return id;
    return await save({ closeAfter: false });
  };

  const refresh = async (aid) => {
    const full = await api.get(`/admin/actualites/${aid}`);
    setPhotos(full.photos || []);
    setDocuments(full.documents || []);
  };

  // Upload multiple de photos (et eventuellement definir la principale).
  const addPhotos = async (e, principale) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";  // permet de re-selectionner les memes fichiers ensuite
    if (files.length === 0) return;
    setError(""); setUploading(true);
    try {
      const aid = await ensureId();
      if (!aid) return;
      for (const file of files) {
        await uploadFile(`/admin/actualites/${aid}/photos`, file,
          { principale: principale ? "true" : "false" });
      }
      await refresh(aid);
    } catch (er) { setError(er.message); }
    finally { setUploading(false); }
  };

  const delPhoto = async (pid) => {
    try { await api.del(`/admin/actualites/${id}/photos/${pid}`); await refresh(id); }
    catch (er) { setError(er.message); }
  };

  // Upload multiple de PDF.
  const addDocs = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    setError(""); setUploading(true);
    try {
      const aid = await ensureId();
      if (!aid) return;
      for (const file of files) {
        await uploadFile(`/admin/actualites/${aid}/documents`, file, {});
      }
      await refresh(aid);
    } catch (er) { setError(er.message); }
    finally { setUploading(false); }
  };

  const delDoc = async (did) => {
    try { await api.del(`/admin/actualites/${id}/documents/${did}`); await refresh(id); }
    catch (er) { setError(er.message); }
  };

  return (
    <div>
      <button className="secondary small mb-2" onClick={onClose}>← Retour à la liste</button>
      <div className="card">
        {error && <div className="error">{error}</div>}
        {info && <div className="success">{info}</div>}
        <div className="field">
          <label>Titre *</label>
          <input value={f.titre} onChange={(e) => set("titre", e.target.value)} maxLength={200} />
        </div>
        <div className="row">
          <div className="field">
            <label>Catégorie</label>
            <select value={f.categorie} onChange={(e) => set("categorie", e.target.value)}>
              {CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Date de publication</label>
            <input type="date" value={f.date_publication}
                   onChange={(e) => set("date_publication", e.target.value)} />
          </div>
        </div>
        <div className="field">
          <label>Date et heure d'expiration (optionnel)</label>
          <input type="datetime-local" value={f.date_expiration}
                 onChange={(e) => set("date_expiration", e.target.value)} />
          <span className="muted" style={{ fontSize: "0.82em" }}>
            Passé cette date, l'actualité est automatiquement archivée et
            n'apparaît plus sur le site. Laissez vide pour ne pas expirer.
          </span>
        </div>
        <div className="field">
          <label>Lien vers une rubrique (optionnel)</label>
          <RubriqueSelect value={f.lien_rubrique}
                          onChange={(v) => set("lien_rubrique", v)} />
        </div>
        <div className="field">
          <label>Chapeau (résumé court)</label>
          <textarea rows={2} value={f.chapeau} onChange={(e) => set("chapeau", e.target.value)}
                    maxLength={500} />
        </div>
        <div className="field">
          <label>Contenu</label>
          <RichEditor value={f.contenu_html} onChange={(html) => set("contenu_html", html)} />
        </div>
        <div className="row">
          <label style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <input type="checkbox" style={{ width: "auto" }} checked={f.publie}
                   onChange={(e) => set("publie", e.target.checked)} /> Publié
          </label>
          <label style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <input type="checkbox" style={{ width: "auto" }} checked={f.epingle}
                   onChange={(e) => set("epingle", e.target.checked)} /> Épinglé en haut
          </label>
        </div>
        <button onClick={() => save({ closeAfter: true })} disabled={busy} className="mt-2">
          {busy ? "Enregistrement…" : "Enregistrer et fermer"}
        </button>
        {!id && (
          <button onClick={() => save({ closeAfter: false })} disabled={busy}
                  className="secondary mt-2" style={{ marginLeft: 8 }}>
            Enregistrer et ajouter des fichiers
          </button>
        )}
      </div>

      {/* Pieces jointes : photos + PDF (multi-fichiers). */}
      <div className="card mt-3">
        <h3>Pièces jointes</h3>
        {!id && (
          <p className="muted">
            Enregistrez d'abord l'actualité (bouton ci-dessus) pour pouvoir
            ajouter des photos et des PDF — ou utilisez « Enregistrer et ajouter des fichiers ».
          </p>
        )}

        <h4 style={{ marginBottom: "0.4rem" }}>Photos</h4>
        <p className="muted" style={{ fontSize: "0.85em" }}>
          Plusieurs photos possibles (max 8, 5 Mo chacune). La photo principale
          s'affiche en tête de l'article.
        </p>
        <div className="row mt-1">
          <div>
            <label>Ajouter des photos</label>
            <input type="file" accept="image/*" multiple
                   disabled={uploading} onChange={(e) => addPhotos(e, false)} />
          </div>
          <div>
            <label>Ajouter comme photo principale</label>
            <input type="file" accept="image/*"
                   disabled={uploading} onChange={(e) => addPhotos(e, true)} />
          </div>
        </div>
        <div className="article-photos mt-2">
          {photos.map((p) => (
            <figure key={p.id}>
              <img src={UPLOADS + p.filename} alt={p.legende || ""} />
              {p.filename === f.image_principale && <span className="muted">★ principale</span>}
              <button className="danger small mt-1" onClick={() => delPhoto(p.id)}>Supprimer</button>
            </figure>
          ))}
        </div>

        <h4 style={{ marginTop: "1.4rem", marginBottom: "0.4rem" }}>Documents PDF</h4>
        <p className="muted" style={{ fontSize: "0.85em" }}>
          Plusieurs PDF possibles (max 12, 15 Mo chacun).
        </p>
        <div className="mt-1">
          <label>Ajouter des PDF</label>
          <input type="file" accept=".pdf,application/pdf" multiple
                 disabled={uploading} onChange={addDocs} />
        </div>
        {uploading && <p className="muted mt-1">Envoi en cours…</p>}
        {documents.length > 0 && (
          <ul className="actu-doc-admin-list mt-2">
            {documents.map((d) => (
              <li key={d.id}>
                <a href={DOCUMENTS_BASE + d.filename} target="_blank" rel="noopener noreferrer">
                  📄 {d.titre || d.filename}
                </a>
                <button className="danger small" style={{ marginLeft: 8 }}
                        onClick={() => delDoc(d.id)}>Supprimer</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ====================== RUBRIQUES ====================== */
function RubriquesAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try { const d = await api.get("/admin/rubriques"); setItems(d.items || []); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.put(`/admin/rubriques/${editing.cle}`, {
        titre: editing.titre, contenu_html: editing.contenu_html,
      });
      setEditing(null); setMsg("Rubrique enregistrée."); load();
    } catch (e) { setMsg(e.message); }
  };

  if (editing) {
    return (
      <div>
        <button className="secondary small mb-2" onClick={() => setEditing(null)}>← Retour</button>
        <div className="card">
          <div className="field">
            <label>Titre</label>
            <input value={editing.titre}
                   onChange={(e) => setEditing({ ...editing, titre: e.target.value })} />
          </div>
          <div className="field">
            <label>Contenu</label>
            <RichEditor value={editing.contenu_html}
                        onChange={(html) => setEditing({ ...editing, contenu_html: html })} />
          </div>
          <button onClick={save}>Enregistrer</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {msg && <div className="success">{msg}</div>}
      <p className="muted mb-2">Pages de contenu affichées dans les rubriques du site.</p>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Clé</th><th>Titre</th><th></th></tr></thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.cle}>
                <td className="muted">{r.cle}</td>
                <td>{r.titre}</td>
                <td><button className="secondary small" onClick={() => setEditing(r)}>Modifier</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ====================== DOCUMENTS ====================== */
function DocumentsAdmin() {
  const [items, setItems] = useState([]);
  const [titre, setTitre] = useState("");
  const [categorie, setCategorie] = useState("general");
  const [file, setFile] = useState(null);
  const [msg, setMsg] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    try { const d = await api.get("/documents"); setItems(d.items || []); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    setMsg("");
    if (!file || !titre.trim()) { setMsg("Titre et fichier obligatoires."); return; }
    try {
      await uploadFile("/admin/documents", file, { titre, categorie });
      setTitre(""); setFile(null); load();
    } catch (e) { setMsg(e.message); }
  };

  const remove = async () => {
    try { await api.del(`/admin/documents/${confirmDel.id}`); setConfirmDel(null); load(); }
    catch (e) { setMsg(e.message); }
  };

  return (
    <div>
      {msg && <div className="error">{msg}</div>}
      <div className="card mb-3">
        <h3>Ajouter un document</h3>
        <div className="row">
          <div className="field"><label>Titre</label>
            <input value={titre} onChange={(e) => setTitre(e.target.value)} /></div>
          <div className="field"><label>Catégorie</label>
            <select value={categorie} onChange={(e) => setCategorie(e.target.value)}>
              <option value="general">Général</option>
              <option value="conseil_municipal">Conseil municipal</option>
              <option value="arrete">Arrêté</option>
              <option value="journal">Journal communal</option>
              <option value="formulaire">Formulaire</option>
            </select>
          </div>
        </div>
        <div className="field"><label>Fichier (PDF, image, doc — max 15 Mo)</label>
          <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} /></div>
        <button onClick={upload}>Téléverser</button>
      </div>

      <div className="table-wrap">
        <table>
          <thead><tr><th>Titre</th><th>Catégorie</th><th>Lien</th><th></th></tr></thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id}>
                <td>{d.titre}</td>
                <td className="muted">{d.categorie}</td>
                <td><a href={`/uploads/documents/${d.filename}`} target="_blank" rel="noopener noreferrer">Ouvrir</a></td>
                <td><button className="danger small" onClick={() => setConfirmDel(d)}>Suppr.</button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="muted">Aucun document.</td></tr>}
          </tbody>
        </table>
      </div>

      {confirmDel && (
        <ConfirmModal titre="Supprimer le document" danger
          message={`Supprimer « ${confirmDel.titre} » ?`} confirmLabel="Supprimer"
          onConfirm={remove} onCancel={() => setConfirmDel(null)} />
      )}
    </div>
  );
}

/* ====================== MESSAGES ====================== */
function MessagesAdmin() {
  const [items, setItems] = useState([]);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    try { const d = await api.get("/admin/messages"); setItems(d.items || []); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (m) => {
    try { await api.patch(`/admin/messages/${m.id}`, { traite: !m.traite }); load(); } catch {}
  };
  const remove = async () => {
    try { await api.del(`/admin/messages/${confirmDel.id}`); setConfirmDel(null); load(); } catch {}
  };

  return (
    <div>
      <p className="muted mb-2">Messages reçus via le formulaire de contact.</p>
      {items.length === 0 && <p className="muted">Aucun message.</p>}
      {items.map((m) => (
        <div key={m.id} className="card mb-2" style={{ opacity: m.traite ? 0.6 : 1 }}>
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem" }}>
            <strong>{m.sujet || "(sans sujet)"}</strong>
            <span className="muted">{new Date(m.created_at).toLocaleString("fr-FR")}</span>
          </div>
          <p className="muted mt-1">
            {m.nom} — <a href={`mailto:${m.email}`}>{m.email}</a>
            {m.telephone ? ` — ${m.telephone}` : ""}
          </p>
          <p className="mt-2" style={{ whiteSpace: "pre-wrap" }}>{m.message}</p>
          <div className="mt-2">
            <button className="secondary small" onClick={() => toggle(m)}>
              {m.traite ? "Marquer non traité" : "Marquer traité"}
            </button>{" "}
            <button className="danger small" onClick={() => setConfirmDel(m)}>Supprimer</button>
          </div>
        </div>
      ))}
      {confirmDel && (
        <ConfirmModal titre="Supprimer le message" danger
          message="Supprimer définitivement ce message ?" confirmLabel="Supprimer"
          onConfirm={remove} onCancel={() => setConfirmDel(null)} />
      )}
    </div>
  );
}

/* ====================== PARAMETRES ====================== */
const PARAM_FIELDS = [
  ["mairie_nom", "Nom de la mairie"], ["mairie_adresse", "Adresse"],
  ["mairie_cp", "Code postal"], ["mairie_ville", "Ville"],
  ["mairie_telephone", "Téléphone"], ["mairie_email", "Email"],
  ["mairie_facebook", "Lien Facebook"],
  ["horaires_lundi", "Horaires lundi"], ["horaires_mardi", "Horaires mardi"],
  ["horaires_mercredi", "Horaires mercredi"], ["horaires_jeudi", "Horaires jeudi"],
  ["horaires_vendredi", "Horaires vendredi"], ["horaires_samedi", "Horaires samedi"],
  ["commune_population", "Population"], ["carte_lat", "Latitude carte"],
  ["carte_lon", "Longitude carte"],
];

function ParametresAdmin() {
  const [vals, setVals] = useState({});
  const [msg, setMsg] = useState("");

  useEffect(() => { api.get("/admin/parametres").then(setVals).catch(() => {}); }, []);

  const save = async () => {
    try {
      await api.put("/admin/parametres", { valeurs: vals });
      setMsg("Paramètres enregistrés.");
    } catch (e) { setMsg(e.message); }
  };

  return (
    <div>
      {msg && <div className="success">{msg}</div>}
      <div className="card">
        <div className="field">
          <label>Texte d'introduction (accueil)</label>
          <textarea rows={3} value={vals.commune_intro || ""}
                    onChange={(e) => setVals({ ...vals, commune_intro: e.target.value })} />
        </div>
        {PARAM_FIELDS.map(([k, label]) => (
          <div className="field" key={k}>
            <label>{label}</label>
            <input value={vals[k] || ""} onChange={(e) => setVals({ ...vals, [k]: e.target.value })} />
          </div>
        ))}
        <button onClick={save}>Enregistrer</button>
      </div>
    </div>
  );
}

/* ====================== UTILISATEURS ====================== */
function UtilisateursAdmin({ meId }) {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [nf, setNf] = useState({ nom: "", prenom: "", email: "", mot_de_passe: "", is_admin: false });
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);
  const [resetFor, setResetFor] = useState(null);
  const [resetPwd, setResetPwd] = useState("");

  const load = useCallback(async () => {
    try { const d = await api.get("/admin/utilisateurs"); setItems(d.items || []); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setErr("");
    if (!nf.nom || !nf.prenom || !nf.email || nf.mot_de_passe.length < 10) {
      setErr("Tous les champs sont requis (mot de passe ≥ 10 caractères)."); return;
    }
    try {
      await api.post("/admin/utilisateurs", nf);
      setCreating(false); setNf({ nom: "", prenom: "", email: "", mot_de_passe: "", is_admin: false });
      setMsg("Compte créé."); load();
    } catch (e) { setErr(e.message); }
  };

  const patch = async (u, body) => {
    try { await api.patch(`/admin/utilisateurs/${u.id}`, body); load(); }
    catch (e) { setMsg(e.message); }
  };

  const remove = async () => {
    try { await api.del(`/admin/utilisateurs/${confirmDel.id}`); setConfirmDel(null); load(); }
    catch (e) { setMsg(e.message); }
  };

  const doReset = async () => {
    if (resetPwd.length < 10) { setMsg("Mot de passe ≥ 10 caractères."); return; }
    try {
      await api.post(`/admin/utilisateurs/${resetFor.id}/reset-password`, { nouveau_mot_de_passe: resetPwd });
      setResetFor(null); setResetPwd(""); setMsg("Mot de passe réinitialisé.");
    } catch (e) { setMsg(e.message); }
  };

  return (
    <div>
      {msg && <div className="success">{msg}</div>}
      <button onClick={() => setCreating((c) => !c)} className="mb-2">
        {creating ? "Annuler" : "+ Nouveau compte"}
      </button>

      {creating && (
        <div className="card mb-3">
          {err && <div className="error">{err}</div>}
          <div className="row">
            <div className="field"><label>Prénom</label>
              <input value={nf.prenom} onChange={(e) => setNf({ ...nf, prenom: e.target.value })} /></div>
            <div className="field"><label>Nom</label>
              <input value={nf.nom} onChange={(e) => setNf({ ...nf, nom: e.target.value })} /></div>
          </div>
          <div className="field"><label>Email</label>
            <input type="email" value={nf.email} onChange={(e) => setNf({ ...nf, email: e.target.value })} /></div>
          <div className="field"><label>Mot de passe (≥ 10 caractères)</label>
            <input type="password" value={nf.mot_de_passe}
                   onChange={(e) => setNf({ ...nf, mot_de_passe: e.target.value })} /></div>
          <label style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <input type="checkbox" style={{ width: "auto" }} checked={nf.is_admin}
                   onChange={(e) => setNf({ ...nf, is_admin: e.target.checked })} /> Administrateur
          </label>
          <button onClick={create} className="mt-2">Créer le compte</button>
        </div>
      )}

      <div className="table-wrap">
        <table>
          <thead><tr><th>Nom</th><th>Email</th><th>Rôle</th><th>Statut</th><th></th></tr></thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id}>
                <td>{u.prenom} {u.nom}</td>
                <td className="muted">{u.email}</td>
                <td>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span>{u.is_admin ? "🛡 Admin" : "Rédacteur"}</span>
                    {u.is_libraire && <span style={{ fontSize: "0.85em", color: "#1d4f91" }}>📚 Libraire</span>}
                    {u.is_barman && <span style={{ fontSize: "0.85em", color: "#b54b3a" }}>🍻 Barman</span>}
                  </div>
                </td>
                <td>
                  <span className={`badge badge-${u.statut === "actif" ? "publie" : "brouillon"}`}>
                    {u.statut}
                  </span>
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="secondary small" onClick={() => setResetFor(u)}>Mot de passe</button>{" "}
                  {u.id !== meId && (
                    <>
                      {u.statut === "actif" ? (
                        <button className="secondary small" onClick={() =>
                          patch(u, { statut: "suspendu" })}>Suspendre</button>
                      ) : u.statut === "suspendu" ? (
                        <button className="secondary small" onClick={() =>
                          patch(u, { statut: "actif" })}>Réactiver</button>
                      ) : (
                        <button className="small" onClick={() =>
                          patch(u, { statut: "actif" })}>Activer</button>
                      )}{" "}
                      <button className="secondary small"
                              onClick={() => patch(u, { is_libraire: !u.is_libraire })}
                              title={u.is_libraire ? "Retirer le rôle libraire" : "Donner le rôle libraire"}>
                        {u.is_libraire ? "− Libraire" : "+ Libraire"}
                      </button>{" "}
                      <button className="secondary small"
                              onClick={() => patch(u, { is_barman: !u.is_barman })}
                              title={u.is_barman ? "Retirer le rôle barman" : "Donner le rôle barman"}>
                        {u.is_barman ? "− Barman" : "+ Barman"}
                      </button>{" "}
                      <button className="danger small" onClick={() => setConfirmDel(u)}>Suppr.</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {confirmDel && (
        <ConfirmModal titre="Supprimer le compte" danger
          message={`Supprimer le compte de ${confirmDel.prenom} ${confirmDel.nom} ?`}
          confirmLabel="Supprimer" onConfirm={remove} onCancel={() => setConfirmDel(null)} />
      )}

      {resetFor && (
        <div className="modal-backdrop" onClick={() => setResetFor(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Réinitialiser le mot de passe</h2>
            <p className="muted">Compte : {resetFor.prenom} {resetFor.nom}</p>
            <div className="field mt-2">
              <label>Nouveau mot de passe (≥ 10 caractères)</label>
              <input type="password" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} />
            </div>
            <div className="modal-actions">
              <button className="secondary" onClick={() => setResetFor(null)}>Annuler</button>
              <button onClick={doReset}>Valider</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ====================== EVENEMENTS (CALENDRIER) ====================== */
const VIS_LABELS = { public: "Public", connectes: "Comptes connectés", groupe: "Groupe(s)" };

function EvenementsAdmin() {
  const [items, setItems] = useState([]);
  const [emojis, setEmojis] = useState([]);
  const [groupes, setGroupes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [participants, setParticipants] = useState(null);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try {
      const d = await api.get("/admin/evenements");
      setItems(d.items || []); setEmojis(d.emojis || []);
      const g = await api.get("/admin/groupes"); setGroupes(g.items || []);
    } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const empty = {
    titre: "", description: "", lieu: "", date_debut: new Date().toISOString().slice(0, 10),
    date_fin: "", heure_debut: "", heure_fin: "", toute_la_journee: false,
    emoji: null, couleur: "#1d4f91", visibilite: "public", inscription_active: false,
    inscription_ouverte_tous: false,  // [lot6]
    publie: true, groupe_ids: [],
  };

  const remove = async () => {
    try { await api.del(`/admin/evenements/${confirmDel.id}`); setConfirmDel(null); load(); }
    catch (e) { setMsg(e.message); }
  };

  const showParticipants = async (e) => {
    try { const d = await api.get(`/admin/evenements/${e.id}/participants`);
      setParticipants({ evt: e, list: d.items || [] }); } catch {}
  };

  if (editing) {
    return <EvenementForm initial={editing} emojis={emojis} groupes={groupes}
                          onClose={() => { setEditing(null); load(); }} />;
  }

  return (
    <div>
      {msg && <div className="error">{msg}</div>}
      <button onClick={() => setEditing(empty)} className="mb-2">+ Nouvel événement</button>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Événement</th><th>Date</th><th>Visibilité</th><th>Inscr.</th><th>État</th><th></th></tr></thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id}>
                <td>{e.emoji ? e.emoji + " " : ""}{e.titre}</td>
                <td>{e.date_debut}{e.date_fin && e.date_fin !== e.date_debut ? ` → ${e.date_fin}` : ""}</td>
                <td>{VIS_LABELS[e.visibilite] || e.visibilite}</td>
                <td>{e.inscription_active
                  ? <button className="secondary small" onClick={() => showParticipants(e)}>{e.nb_participants} inscrit(s)</button>
                  : "—"}</td>
                <td><span className={`badge ${e.publie ? "badge-publie" : "badge-brouillon"}`}>{e.publie ? "Publié" : "Brouillon"}</span></td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="secondary small" onClick={() =>
                    setEditing({ ...empty, ...e, groupe_ids: e.groupe_ids || [] })
                  }>Modifier</button>{" "}
                  <button className="danger small" onClick={() => setConfirmDel(e)}>Suppr.</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="muted">Aucun événement.</td></tr>}
          </tbody>
        </table>
      </div>

      {confirmDel && (
        <ConfirmModal titre="Supprimer l'événement" danger
          message={`Supprimer « ${confirmDel.titre} » ?`} confirmLabel="Supprimer"
          onConfirm={remove} onCancel={() => setConfirmDel(null)} />
      )}

      {participants && (
        <div className="modal-backdrop" onClick={() => setParticipants(null)}>
          <div className="modal" onClick={(ev) => ev.stopPropagation()}>
            <h2>Inscrits — {participants.evt.titre}</h2>
            {participants.list.length === 0 ? <p className="muted">Aucun inscrit.</p> : (
              <table><tbody>
                {participants.list.map((p) => (
                  <tr key={p.id}><td>{p.prenom} {p.nom}</td><td className="muted">{p.email}</td></tr>
                ))}
              </tbody></table>
            )}
            <div className="modal-actions">
              <button className="secondary" onClick={() => setParticipants(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EvenementForm({ initial, emojis, groupes, onClose }) {
  const [f, setF] = useState({ ...initial });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const toggleGroupe = (gid) => {
    const cur = f.groupe_ids || [];
    set("groupe_ids", cur.includes(gid) ? cur.filter((x) => x !== gid) : [...cur, gid]);
  };

  const save = async () => {
    setError("");
    if (!f.titre.trim()) { setError("Le titre est obligatoire."); return; }
    if (f.visibilite === "groupe" && (!f.groupe_ids || f.groupe_ids.length === 0)) {
      setError("Sélectionnez au moins un groupe pour une visibilité « groupe »."); return;
    }
    setBusy(true);
    const payload = {
      titre: f.titre, description: f.description || "", lieu: f.lieu || "",
      date_debut: f.date_debut, date_fin: f.date_fin || null,
      heure_debut: f.heure_debut || null, heure_fin: f.heure_fin || null,
      toute_la_journee: !!f.toute_la_journee, emoji: f.emoji || null,
      couleur: f.couleur || "#1d4f91", visibilite: f.visibilite,
      inscription_active: !!f.inscription_active,
      inscription_ouverte_tous: !!f.inscription_ouverte_tous,  // [lot6]
      publie: !!f.publie,
      groupe_ids: f.groupe_ids || [],
    };
    try {
      if (f.id) await api.put(`/admin/evenements/${f.id}`, payload);
      else await api.post("/admin/evenements", payload);
      onClose();
    } catch (e) { setError(e.message || "Erreur."); } finally { setBusy(false); }
  };

  return (
    <div>
      <button className="secondary small mb-2" onClick={onClose}>← Retour à la liste</button>
      <div className="card">
        {error && <div className="error">{error}</div>}
        <div className="field"><label>Titre *</label>
          <input value={f.titre} onChange={(e) => set("titre", e.target.value)} maxLength={200} /></div>

        <div className="field">
          <label>Emoji (optionnel)</label>
          <div className="emoji-grid">
            <button type="button" className={`emoji-btn ${!f.emoji ? "sel" : ""}`}
                    onClick={() => set("emoji", null)} title="Aucun">∅</button>
            {emojis.map((em) => (
              <button type="button" key={em}
                      className={`emoji-btn ${f.emoji === em ? "sel" : ""}`}
                      onClick={() => set("emoji", em)}>{em}</button>
            ))}
          </div>
        </div>

        <div className="row">
          <div className="field"><label>Date de début *</label>
            <input type="date" value={f.date_debut} onChange={(e) => set("date_debut", e.target.value)} /></div>
          <div className="field"><label>Date de fin</label>
            <input type="date" value={f.date_fin || ""} onChange={(e) => set("date_fin", e.target.value)} /></div>
        </div>

        <label style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginBottom: "0.6rem" }}>
          <input type="checkbox" style={{ width: "auto" }} checked={!!f.toute_la_journee}
                 onChange={(e) => set("toute_la_journee", e.target.checked)} /> Toute la journée
        </label>

        {!f.toute_la_journee && (
          <div className="row">
            <div className="field"><label>Heure de début</label>
              <input type="time" value={f.heure_debut || ""} onChange={(e) => set("heure_debut", e.target.value)} /></div>
            <div className="field"><label>Heure de fin</label>
              <input type="time" value={f.heure_fin || ""} onChange={(e) => set("heure_fin", e.target.value)} /></div>
          </div>
        )}

        <div className="field"><label>Lieu</label>
          <input value={f.lieu || ""} onChange={(e) => set("lieu", e.target.value)} maxLength={200} /></div>
        <div className="field"><label>Description</label>
          <textarea rows={4} value={f.description || ""} onChange={(e) => set("description", e.target.value)} maxLength={5000} /></div>

        <div className="row">
          <div className="field"><label>Couleur</label>
            <input type="color" value={f.couleur || "#1d4f91"} onChange={(e) => set("couleur", e.target.value)} /></div>
          <div className="field"><label>Visibilité</label>
            <select value={f.visibilite} onChange={(e) => set("visibilite", e.target.value)}>
              <option value="public">Public (tout le monde)</option>
              <option value="connectes">Comptes connectés</option>
              <option value="groupe">Groupe(s) précis</option>
            </select>
          </div>
        </div>

        {f.visibilite === "groupe" && (
          <div className="field">
            <label>Groupes destinataires</label>
            {groupes.length === 0 ? <p className="muted">Aucun groupe. Créez-en dans l'onglet Groupes.</p> : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                {groupes.map((g) => (
                  <label key={g.id} style={{ display: "flex", gap: "0.4rem", alignItems: "center",
                    border: "1px solid var(--line)", borderRadius: 6, padding: "0.3rem 0.6rem" }}>
                    <input type="checkbox" style={{ width: "auto" }}
                           checked={(f.groupe_ids || []).includes(g.id)}
                           onChange={() => toggleGroupe(g.id)} /> {g.nom}
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="row mt-2">
          <label style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <input type="checkbox" style={{ width: "auto" }} checked={!!f.inscription_active}
                   onChange={(e) => set("inscription_active", e.target.checked)} /> Autoriser les inscriptions
          </label>
          {/* [lot6] mode d'inscription : connecte requis ou ouvert a tous */}
          {f.inscription_active && (
            <label style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <input type="checkbox" style={{ width: "auto" }} checked={!!f.inscription_ouverte_tous}
                     onChange={(e) => set("inscription_ouverte_tous", e.target.checked)} />
              Ouverte à tous (sans compte : nom + email)
            </label>
          )}
          <label style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <input type="checkbox" style={{ width: "auto" }} checked={!!f.publie}
                   onChange={(e) => set("publie", e.target.checked)} /> Publié
          </label>
        </div>

        <button onClick={save} disabled={busy} className="mt-3">
          {busy ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>
    </div>
  );
}

/* ====================== GROUPES ====================== */
function GroupesAdmin() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [nf, setNf] = useState({ nom: "", description: "", couleur: "#1d4f91" });
  const [membresFor, setMembresFor] = useState(null);
  const [selMembres, setSelMembres] = useState([]);
  const [confirmDel, setConfirmDel] = useState(null);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try {
      const d = await api.get("/admin/groupes"); setItems(d.items || []);
      const u = await api.get("/admin/utilisateurs"); setUsers((u.items || []).filter((x) => !x.is_admin || true));
    } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setMsg("");
    if (!nf.nom.trim()) { setMsg("Nom requis."); return; }
    try { await api.post("/admin/groupes", nf); setCreating(false);
      setNf({ nom: "", description: "", couleur: "#1d4f91" }); load(); }
    catch (e) { setMsg(e.message); }
  };

  const remove = async () => {
    try { await api.del(`/admin/groupes/${confirmDel.id}`); setConfirmDel(null); load(); }
    catch (e) { setMsg(e.message); }
  };

  const openMembres = async (g) => {
    try { const d = await api.get(`/admin/groupes/${g.id}/membres`);
      setMembresFor(g); setSelMembres((d.items || []).map((m) => m.id)); } catch {}
  };

  const saveMembres = async () => {
    try { await api.put(`/admin/groupes/${membresFor.id}/membres`, { utilisateur_ids: selMembres });
      setMembresFor(null); load(); } catch (e) { setMsg(e.message); }
  };

  const toggleMembre = (uid) =>
    setSelMembres((s) => s.includes(uid) ? s.filter((x) => x !== uid) : [...s, uid]);

  return (
    <div>
      {msg && <div className="error">{msg}</div>}
      <button onClick={() => setCreating((c) => !c)} className="mb-2">
        {creating ? "Annuler" : "+ Nouveau groupe"}
      </button>
      {creating && (
        <div className="card mb-3">
          <div className="field"><label>Nom du groupe</label>
            <input value={nf.nom} onChange={(e) => setNf({ ...nf, nom: e.target.value })} /></div>
          <div className="field"><label>Description</label>
            <input value={nf.description} onChange={(e) => setNf({ ...nf, description: e.target.value })} /></div>
          <button onClick={create}>Créer</button>
        </div>
      )}
      <div className="table-wrap">
        <table>
          <thead><tr><th>Groupe</th><th>Membres</th><th></th></tr></thead>
          <tbody>
            {items.map((g) => (
              <tr key={g.id}>
                <td>{g.nom}{g.description ? <span className="muted"> — {g.description}</span> : ""}</td>
                <td>{g.nb_membres}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="secondary small" onClick={() => openMembres(g)}>Membres</button>{" "}
                  <button className="danger small" onClick={() => setConfirmDel(g)}>Suppr.</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={3} className="muted">Aucun groupe.</td></tr>}
          </tbody>
        </table>
      </div>

      {confirmDel && (
        <ConfirmModal titre="Supprimer le groupe" danger
          message={`Supprimer « ${confirmDel.nom} » ?`} confirmLabel="Supprimer"
          onConfirm={remove} onCancel={() => setConfirmDel(null)} />
      )}

      {membresFor && (
        <div className="modal-backdrop" onClick={() => setMembresFor(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Membres — {membresFor.nom}</h2>
            {users.length === 0 ? <p className="muted">Aucun compte disponible.</p> : (
              <div style={{ maxHeight: 320, overflowY: "auto" }}>
                {users.map((u) => (
                  <label key={u.id} style={{ display: "flex", gap: "0.5rem", alignItems: "center", padding: "0.3rem 0" }}>
                    <input type="checkbox" style={{ width: "auto" }}
                           checked={selMembres.includes(u.id)} onChange={() => toggleMembre(u.id)} />
                    {u.prenom} {u.nom} <span className="muted">({u.email})</span>
                  </label>
                ))}
              </div>
            )}
            <div className="modal-actions">
              <button className="secondary" onClick={() => setMembresFor(null)}>Annuler</button>
              <button onClick={saveMembres}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ====================== INSCRIPTIONS ====================== */
function InscriptionsAdmin() {
  const [items, setItems] = useState([]);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try { const d = await api.get("/admin/inscriptions"); setItems(d.items || []); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    try { await api.post(`/admin/inscriptions/${id}/${action}`, {}); load(); }
    catch (e) { setMsg(e.message); }
  };

  return (
    <div>
      {msg && <div className="error">{msg}</div>}
      <p className="muted mb-2">Demandes de création de compte en attente de validation.</p>
      {items.length === 0 ? <p className="muted">Aucune demande en attente.</p> : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Nom</th><th>Email</th><th>Demande</th><th></th></tr></thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id}>
                  <td>{u.prenom} {u.nom}</td>
                  <td className="muted">{u.email}</td>
                  <td className="muted">{new Date(u.created_at).toLocaleDateString("fr-FR")}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="small" onClick={() => act(u.id, "valider")}>Valider</button>{" "}
                    <button className="danger small" onClick={() => act(u.id, "refuser")}>Refuser</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ====================== SIGNALEMENTS (ADMIN) ====================== */
const SIG_CAT_LABELS = {
  voirie: "Voirie", proprete: "Propreté", eclairage: "Éclairage",
  espaces_verts: "Espaces verts", mobilier: "Mobilier", autre: "Autre",
};
const SIG_STATUTS = {
  en_attente_validation: { label: "En attente", color: "#b88a2e" },
  nouveau: { label: "Nouveau", color: "#b54b3a" },
  pris_en_compte: { label: "Pris en compte", color: "#d9a521" },
  en_cours: { label: "En cours", color: "#1d4f91" },
  resolu: { label: "Résolu", color: "#2f7d4f" },
  refuse: { label: "Refusé", color: "#888" },
};

function SignalementsAdmin() {
  const params = useParams2();
  const mapRef = useRef(null);
  const leafletMap = useRef(null);
  const markersLayer = useRef(null);
  const markersIndex = useRef({});  // id -> marker (pour ouvrir le popup depuis la liste)

  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [filtreCat, setFiltreCat] = useState("");
  const [filtreStatut, setFiltreStatut] = useState("");
  const [msg, setMsg] = useState("");

  const lat = parseFloat(params.carte_lat || "48.1097");
  const lon = parseFloat(params.carte_lon || "0.0856");

  const load = useCallback(async () => {
    try { const d = await api.get("/admin/signalements"); setItems(d.items || []); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  // Init carte (une seule fois)
  useEffect(() => {
    const L = window.L;
    if (!L || !mapRef.current || leafletMap.current) return;
    leafletMap.current = L.map(mapRef.current).setView([lat, lon], 14);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap", maxZoom: 19,
    }).addTo(leafletMap.current);
    markersLayer.current = L.layerGroup().addTo(leafletMap.current);
    return () => {
      if (leafletMap.current) { leafletMap.current.remove(); leafletMap.current = null; }
    };
    // eslint-disable-next-line
  }, []);

  // Sync marqueurs <-> items + filtres
  useEffect(() => {
    const L = window.L;
    if (!L || !markersLayer.current) return;
    markersLayer.current.clearLayers();
    markersIndex.current = {};
    items
      .filter((s) => !filtreCat || s.categorie === filtreCat)
      .filter((s) => !filtreStatut || s.statut === filtreStatut)
      .forEach((s) => {
        const m = L.marker([s.latitude, s.longitude], {
          icon: buildMarkerIcon(L, s.categorie, s.statut, { selected: activeId === s.id }),
        });
        m.on("click", () => { setActiveId(s.id); setEditing(s); });
        m.addTo(markersLayer.current);
        markersIndex.current[s.id] = m;
      });
  }, [items, filtreCat, filtreStatut, activeId]);

  const act = async (id, action) => {
    try { await api.post(`/admin/signalements/${id}/${action}`, {}); load(); }
    catch (e) { setMsg(e.message); }
  };
  const setStatut = async (id, statut) => {
    try { await api.patch(`/admin/signalements/${id}/statut`, { statut }); load(); }
    catch (e) { setMsg(e.message); }
  };
  const setVisibilite = async (id, visibilite) => {  // [signal-vis]
    try { await api.patch(`/admin/signalements/${id}/visibilite`, { visibilite }); load(); }
    catch (e) { setMsg(e.message); }
  };
  const remove = async () => {
    try { await api.del(`/admin/signalements/${confirmDel.id}`); setConfirmDel(null); load(); }
    catch (e) { setMsg(e.message); }
  };

  const filtered = items
    .filter((s) => !filtreCat || s.categorie === filtreCat)
    .filter((s) => !filtreStatut || s.statut === filtreStatut);

  const onRowClick = (s) => {
    setActiveId(s.id);
    if (leafletMap.current) leafletMap.current.setView([s.latitude, s.longitude], 17);
  };

  return (
    <div>
      {msg && <div className="error">{msg}</div>}

      <div className="signal-filters mb-2">
        <select value={filtreCat} onChange={(e) => setFiltreCat(e.target.value)}>
          <option value="">Toutes catégories</option>
          {Object.entries(SIG_CAT_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select value={filtreStatut} onChange={(e) => setFiltreStatut(e.target.value)}>
          <option value="">Tous statuts</option>
          {Object.entries(SIG_STATUTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <span className="muted">{filtered.length} signalement(s)</span>
      </div>

      <div className="signal-split">
        <div ref={mapRef} className="signal-map" />

        <div className="signal-list-pane">
          {filtered.length === 0 && <p className="muted" style={{ padding: "0.6rem" }}>Aucun signalement.</p>}
          {filtered.map((s) => {
            const st = SIG_STATUTS[s.statut] || {};
            const isActive = activeId === s.id;
            const isPending = s.statut === "en_attente_validation";
            return (
              <div key={s.id} className={`signal-row ${isActive ? "active" : ""}`}
                   onClick={() => onRowClick(s)}>
                <span className="signal-row-dot" style={{ background: st.color }} />
                <div className="signal-row-main">
                  <div className="signal-row-title">{s.titre}</div>
                  <div className="signal-row-meta">
                    {SIG_CAT_LABELS[s.categorie]} · {st.label}
                    {s.auteur_nom ? ` · ${s.auteur_nom}` : ""}
                  </div>
                </div>
                <div className="signal-row-actions" onClick={(e) => e.stopPropagation()}>
                  {isPending ? (
                    <>
                      <button className="small" onClick={() => act(s.id, "valider")}>✓</button>
                      <button className="danger small" onClick={() => act(s.id, "refuser")}>✕</button>
                    </>
                  ) : (
                    <>
                      <select value={s.statut} onChange={(e) => setStatut(s.id, e.target.value)}
                              style={{ fontSize: "0.78rem", padding: "0.2rem 0.3rem",
                                       borderLeft: `4px solid ${st.color}`, width: "auto" }}>
                        <option value="nouveau">Nouveau</option>
                        <option value="pris_en_compte">Pris en compte</option>
                        <option value="en_cours">En cours</option>
                        <option value="resolu">Résolu</option>
                      </select>
                      {/* [signal-vis] visibilite : resolu force admin-only */}
                      {s.statut === "resolu" ? (
                        <span className="signal-vis-locked muted" title="Un signalement résolu n'est visible que des administrateurs">🔒 admins</span>
                      ) : (
                        <select value={s.visibilite || "admins"}
                                onChange={(e) => setVisibilite(s.id, e.target.value)}
                                title="Qui peut voir ce signalement ?"
                                style={{ fontSize: "0.78rem", padding: "0.2rem 0.3rem", width: "auto" }}>
                          <option value="public">👁 Public</option>
                          <option value="connectes">👤 Connectés</option>
                          <option value="admins">🔒 Admins</option>
                        </select>
                      )}
                      <button className="secondary small" onClick={() => setEditing(s)}>i</button>
                      <button className="danger small" onClick={() => setConfirmDel(s)}>🗑</button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="signal-legend">
        {Object.entries(SIG_STATUTS).map(([k, v]) => (
          <span key={k} className="signal-legend-item">
            <span className="signal-pin" style={{ background: v.color }} /> {v.label}
          </span>
        ))}
      </div>

      {confirmDel && (
        <ConfirmModal titre="Supprimer le signalement" danger
          message={`Supprimer « ${confirmDel.titre} » ?`} confirmLabel="Supprimer"
          onConfirm={remove} onCancel={() => setConfirmDel(null)} />
      )}

      {editing && <SignalDetailAdmin signal={editing} onReload={load}
                                     onClose={() => setEditing(null)} />}
    </div>
  );
}

function SignalDetailAdmin({ signal, onReload, onClose }) {
  const [commentaire, setCommentaire] = useState(signal.commentaire_mairie || "");
  const [msg, setMsg] = useState("");
  const save = async () => {
    try {
      await api.patch(`/admin/signalements/${signal.id}/commentaire`,
                      { commentaire_mairie: commentaire });
      setMsg("Commentaire enregistré.");
      onReload();
    } catch (e) { setMsg(e.message); }
  };
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{signal.titre}</h2>
        <p className="muted">
          {SIG_CAT_LABELS[signal.categorie]} ·
          Par {signal.auteur_nom || "Anonyme"}
          {signal.auteur_email && ` (${signal.auteur_email})`}
        </p>
        <p className="muted">
          Position : {signal.latitude.toFixed(5)}, {signal.longitude.toFixed(5)}
        </p>
        {signal.description && (
          <p className="mt-2" style={{ whiteSpace: "pre-wrap" }}>{signal.description}</p>
        )}
        {signal.photo_filename && (
          <img src={"/uploads/signalements/" + signal.photo_filename} alt=""
               style={{ width: "100%", maxWidth: 420, borderRadius: 6, marginTop: 12 }} />
        )}
        <div className="field mt-2">
          <label>Mot de la mairie (visible publiquement)</label>
          <textarea rows={3} value={commentaire} maxLength={1000}
                    onChange={(e) => setCommentaire(e.target.value)} />
        </div>
        {msg && <div className="success">{msg}</div>}
        <div className="modal-actions">
          <button className="secondary" onClick={onClose}>Fermer</button>
          <button onClick={save}>Enregistrer le commentaire</button>
        </div>
      </div>
    </div>
  );
}
